import React, { useMemo, useState } from 'react';

import type { Leaves } from '@aline/common';
import { Select } from '@aline/common';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, ListItemText, Typography } from '@mui/material';
import type { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import { matchSorter } from 'match-sorter';
import { useAsync } from 'react-use';
import { AlineAutocompleteInput, AlineAvatar, Spacer } from 'src/components';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import type { Apollo } from 'src/gql/generated';
import { always, doNothing, getEntityDisplayDetails } from 'src/helpers';
import { useGetWorkflowPermissions } from 'src/hooks';
import { roleAvatarColor } from 'src/theme';
import type { AccountSubset, UserDefinedRoleSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

export const unassignedCollaboratorString = 'Unassigned';

export interface NewCollaboratorOption {
  __typename: 'NewCollaborator';
  email: string;
}
type CollaboratorAction =
  | Pick<Apollo.WorkflowRecipient, 'id' | '__typename' | 'type'>
  | Pick<Apollo.Task, 'id' | '__typename'>;
export interface ExistingCollaboratorOption extends Pick<
  Apollo.WorkflowPermission,
  'id' | 'isAssignee' | 'timeliness' | '__typename'
> {
  account: AccountSubset | null;
  actions: CollaboratorAction[];
  role: UserDefinedRoleSubset | null;
  disabled?: boolean;
}
export type CollaboratorOption = NewCollaboratorOption | ExistingCollaboratorOption;

type CustomAutocompleteProps = AutocompleteProps<CollaboratorOption, false, false, false>;
type FilterOptions = CustomAutocompleteProps['filterOptions'];
export type CollaboratorAutocompleteValue = Omit<ExistingCollaboratorOption, 'actions'>;

export interface WorkflowCollaboratorsAutoCompleteProps {
  onChange: (
    event: React.ChangeEvent<unknown>,
    value: CollaboratorOption | null,
    reason: AutocompleteChangeReason
  ) => void;
  open: boolean;
  value: CollaboratorAutocompleteValue | null;
  workflowId: UUID;
  blurOnSelect?: CustomAutocompleteProps['blurOnSelect'];
  classes?: CustomAutocompleteProps['classes'];
  className?: string;
  clearInputOnBlur?: boolean;
  clearOnBlur?: boolean;
  dataCy?: string;
  disableCloseOnSelect?: boolean;
  disabled?: boolean;
  disablePortal?: boolean;
  filterPred?: (collaborator: CollaboratorOption) => boolean;
  fullWidth?: boolean;
  getOptionDisabled?: CustomAutocompleteProps['getOptionDisabled'];
  onChangeInput?: (inputValue: string) => void;
  onClose?: () => void;
  placeholder?: string;
  PopperComponent?: NonNullable<CustomAutocompleteProps['slots']>['popper'];
  refetch?: boolean;
  renderInput?: CustomAutocompleteProps['renderInput'];
  renderOption?: (option: CollaboratorOption) => React.ReactNode;
}

export function WorkflowCollaboratorsAutoComplete(
  props: WorkflowCollaboratorsAutoCompleteProps
): JSX.Element | null {
  const {
    PopperComponent,
    blurOnSelect = true,
    className,
    classes,
    clearInputOnBlur = false,
    clearOnBlur = false,
    dataCy,
    disableCloseOnSelect,
    disablePortal,
    disabled,
    filterPred = always,
    fullWidth = true,
    getOptionDisabled,
    onChange,
    onChangeInput = doNothing,
    onClose,
    open,
    placeholder,
    refetch,
    renderInput,
    renderOption,
    value,
    workflowId,
  } = props;

  const [inputValue, setInputValue] = useState<string>('');
  const [highlightedOption, setHighlightedOption] = useState<CollaboratorOption | null>(null);

  const {
    loading,
    permissions,
    refetch: refetchPermissions,
  } = useGetWorkflowPermissions(workflowId, { skip: !open });
  const options = useMemo((): CollaboratorOption[] => {
    const filteredPermissions = permissions.filter(filterPred);
    return [...filteredPermissions].sort((a, b) => {
      const aName = a.account
        ? getEntityDisplayDetails(a.account).primary
        : (a.role?.roleName ?? unassignedCollaboratorString);
      const bName = b.account
        ? getEntityDisplayDetails(b.account).primary
        : (b.role?.roleName ?? unassignedCollaboratorString);
      return aName.localeCompare(bName);
    });
  }, [filterPred, permissions]);

  useAsync(async () => {
    if (refetch) await refetchPermissions();
  }, [refetch]);

  const filterOptionsAndSuggestIfNeeded: FilterOptions = (options, filterOptionsState) => {
    // Order of keys matters and sets the search ranking.
    const optionKeys: Leaves<ExistingCollaboratorOption>[] = [
      'account.displayName',
      'account.name',
      'account.email',
      'role.roleName',
    ];
    const sortedOptions = matchSorter<CollaboratorOption>(options, inputValue, {
      keys: optionKeys,
    });

    return [
      ...sortedOptions,
      {
        __typename: 'NewCollaborator',
        email: filterOptionsState.inputValue,
      } satisfies NewCollaboratorOption,
    ];
  };

  function getPlaceholder(): string {
    return placeholder ?? 'Search workflow collaborators...';
  }

  return (
    <Autocomplete<CollaboratorOption, false, false, false>
      blurOnSelect={blurOnSelect}
      className={className}
      classes={classes}
      clearOnBlur={clearOnBlur}
      data-cy={dataCy}
      disableCloseOnSelect={disableCloseOnSelect}
      disablePortal={disablePortal}
      disabled={disabled}
      filterOptions={filterOptionsAndSuggestIfNeeded}
      fullWidth={fullWidth}
      getOptionDisabled={getOptionDisabled}
      getOptionKey={option => {
        switch (option.__typename) {
          case 'NewCollaborator':
            return option.email;
          case 'WorkflowPermission':
            return option.id;
        }
      }}
      getOptionLabel={option =>
        option.__typename === 'NewCollaborator'
          ? option.email
          : option.account?.__typename === 'Org'
            ? option.account.name
            : (option.account?.email ?? option.role?.roleName ?? '')
      }
      inputValue={inputValue}
      isOptionEqualToValue={(option, value): boolean => {
        return 'id' in option && 'id' in value && option.id === value.id;
      }}
      loading={loading}
      open={open}
      options={options}
      popupIcon={<NoDropdownIcon />}
      renderInput={params => {
        const placeholder = getPlaceholder();
        if (renderInput) return renderInput(params);

        return <AlineAutocompleteInput placeholder={placeholder} {...params} />;
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        if (renderOption) {
          return (
            <li key={key} {...optionProps}>
              {renderOption(option)}
            </li>
          );
        }

        return (
          <li key={key} {...optionProps}>
            <CollaboratorAutoCompleteOption
              highlightedOptionId={
                highlightedOption && 'id' in highlightedOption ? highlightedOption.id : undefined
              }
              option={option}
            />
          </li>
        );
      }}
      slotProps={{
        listbox: {
          'data-cy': Select.components.fieldRecipientsAutocomplete.listBox,
        },
      }}
      slots={PopperComponent ? { popper: PopperComponent } : undefined}
      value={permissions.find(p => p.id === value?.id) ?? null}
      onBlur={() => clearInputOnBlur && setInputValue('')}
      onChange={onChange}
      onClose={onClose}
      onHighlightChange={(_, option) => setHighlightedOption(option)}
      onInputChange={(_, inputValue, reason) => {
        if (reason === 'reset' || reason === 'selectOption') return;
        setInputValue(inputValue);
        onChangeInput(inputValue);
      }}
    />
  );
}

function NoDropdownIcon(): JSX.Element | null {
  return null;
}

export const useCollabOptionStyles = makeStyles()(theme => ({
  icon: {
    height: 18,
    width: 18,
  },
  iconCircle: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    // Use min/max b/c when typing long strings in the autocomplete it preserves height/width and using normal CSS height/width does not.
    maxHeight: 24,
    maxWidth: 24,
    minHeight: 24,
    minWidth: 24,
  },
  newUser: {
    ...theme.appTypography.buttonSmall,
    color: theme.appTypography.buttonSmall.color,
    whiteSpace: 'nowrap',
  },
  optionContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  secondaryText: {
    color: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
  },
}));

interface NewCollaboratorOptionProps {
  newCollaboratorText?: string;
}

export function NewCollaboratorOption({
  newCollaboratorText = 'Add Collaborator to Steps',
}: NewCollaboratorOptionProps): JSX.Element {
  const { classes } = useCollabOptionStyles();

  return (
    <div
      className={classes.optionContainer}
      data-cy={Select.components.collaboratorsAutocomplete.addCollaboratorButton}
    >
      <div className={classes.iconCircle}>
        <CrossIcon className={classes.icon} />
      </div>
      <Typography className={classes.newUser}>{newCollaboratorText}</Typography>
    </div>
  );
}

interface CollaboratorOptionProps {
  option: CollaboratorOption;
  highlightedOptionId?: UUID;
  newCollaboratorText?: string;
}

export function CollaboratorAutoCompleteOption({
  highlightedOptionId,
  newCollaboratorText,
  option,
}: CollaboratorOptionProps): JSX.Element {
  const { classes } = useCollabOptionStyles();
  const isNewCollaboratorOption = option.__typename === 'NewCollaborator';

  if (isNewCollaboratorOption)
    return <NewCollaboratorOption newCollaboratorText={newCollaboratorText} />;

  const { avatarColor, primaryText, secondaryText } = getCollaboratorDisplayDetails(option);

  return (
    <div
      className={classes.optionContainer}
      data-cy={Select.components.collaboratorsAutocomplete.collaboratorOption}
    >
      <AlineAvatar
        avatarInfo={option.account ?? option.role}
        styles={{ backgroundColor: avatarColor, size: 'md' }}
      />
      <ListItemText
        classes={{ secondary: classes.secondaryText }}
        data-cy="collaborator-option-text"
        primary={primaryText}
        secondary={secondaryText}
        slotProps={{
          primary: { align: 'left', noWrap: true, variant: 'body1' },
          secondary: { align: 'left', noWrap: true },
        }}
      />
      <Spacer />
      <Action highlightedOptionId={highlightedOptionId} option={option} />
    </div>
  );
}

export const useCollabActionStyles = makeStyles()(theme => ({
  assignButton: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  assignText: {
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 600,
  },
  newRole: {
    ...theme.appTypography.buttonSmall,
    whiteSpace: 'nowrap',
  },
}));

interface ActionProps {
  option: ExistingCollaboratorOption;
  highlightedOptionId?: UUID;
}

function Action({ highlightedOptionId, option }: ActionProps): JSX.Element {
  const { classes } = useCollabActionStyles();
  const highlighted = 'id' in option && option.id === highlightedOptionId;

  return (
    <div
      className={classes.assignButton}
      style={{ visibility: highlighted ? 'visible' : 'hidden' }}
    >
      <Typography className={classes.assignText}>Assign</Typography>
    </div>
  );
}

export function getCollaboratorDisplayDetails(collaborator: CollaboratorAutocompleteValue | null): {
  primaryText: string;
  avatarColor?: string;
  secondaryText?: string;
} {
  const isAssignedToAccount = Boolean(collaborator?.account);
  const primaryText =
    collaborator?.account?.__typename === 'Org'
      ? collaborator.account.name
      : (collaborator?.account?.displayName ??
        collaborator?.role?.roleName ??
        unassignedCollaboratorString);
  const secondaryText = isAssignedToAccount ? collaborator?.role?.roleName : undefined;
  const avatarColor = collaborator?.account
    ? collaborator.account.avatarColor
    : collaborator?.role
      ? roleAvatarColor
      : undefined;

  return { avatarColor, primaryText, secondaryText };
}
